const ALIAS_PREFIX_PATTERN = /^\/o\/[^/]+/;

// Query params that change the SSR'd content of listing pages (news, staff,
// live-feed, athletics). Paginated/filtered views must self-canonicalize with
// these params — canonicalizing page 2..N onto page 1 tells Google the deeper
// pages are duplicates and suppresses crawling of content linked only there.
const CANONICAL_QUERY_PARAMS = ['page_no', 'filter_id', 'filter_ids'];

const normalizeAlias = (alias) => alias?.toString().toLowerCase().replace(/-/g, ' ');

const normalizeDomain = (domain) => domain?.toLowerCase().replace(/^www\./, '');

/**
 * Whether the host is one that `layouts/default.vue` marks `robots: noindex`
 * (staging/pre-launch `sites.thrillshare` domains). Canonicals must not be
 * emitted there: they would point at production domains, and Google advises
 * against combining noindex with a canonical to another page.
 * @param {string} domain - The request host.
 * @returns {boolean}
 */
export const isNoIndexDomain = (domain) => !!domain?.includes('sites.thrillshare');

// Matches a canonical managed by the site's custom embed code, whether authored
// as markup (<link rel="canonical" …>) or injected via JS (link.rel = 'canonical').
const CUSTOM_CANONICAL_PATTERN = /rel\s*=\s*\\?["']?canonical/i;

/**
 * Whether any of the site's custom embed codes already manages canonical tags.
 * Districts worked around the platform's missing canonicals with custom code;
 * emitting a platform canonical on top would give crawlers two conflicting
 * tags, which Google resolves unpredictably. Those sites keep their own until
 * the workaround is removed.
 * @param {Array<string>} customCodes - `settings.embedCodes.customCodes` entries.
 * @returns {boolean}
 */
export const customCodeManagesCanonical = (customCodes) =>
  (customCodes || []).some((code) => typeof code === 'string' && CUSTOM_CANONICAL_PATTERN.test(code));

const buildCanonicalQueryString = (query) => {
  if (!query) return '';
  const params = new URLSearchParams();
  CANONICAL_QUERY_PARAMS.forEach((key) => {
    const value = query[key];
    // page_no=1 is the same content as the bare listing URL — keep one canonical form.
    if (value === undefined || value === null || value === '' || (key === 'page_no' && String(value) === '1')) return;
    params.set(key, value);
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Finds the organization the current request is rendering content for.
 * @param {{ alias: string|null, domain: string, organizations: Array<object> }} params
 * @param {string|null} params.alias - The `/o/{alias}` segment of the request URL (hyphens may stand in for spaces), or null when the org was resolved by domain alone.
 * @param {string} params.domain - The request host. ex: 'www.myschool.org'
 * @param {Array<object>} params.organizations - The org family list from the site structure `domain.organizations`, each with `alias`, `org_url`, `path_prefix`, `primary`.
 * @returns {object|null} - The matching organization or null.
 */
export const resolveCanonicalOrg = ({ alias, domain, organizations }) => {
  const orgs = organizations || [];
  if (alias) {
    const normalizedAlias = normalizeAlias(alias);
    return orgs.find((org) => org?.alias && normalizeAlias(org.alias) === normalizedAlias) || null;
  }
  if (domain) {
    // www-insensitive so www.district.org and district.org consolidate onto the org_url form.
    const normalizedDomain = normalizeDomain(domain);
    return orgs.find((org) => org?.org_url && normalizeDomain(org.org_url) === normalizedDomain) || null;
  }
  return null;
};

/**
 * Builds the canonical URL for the currently rendered route.
 *
 * Every page resolves from any org domain in the family (and both with and
 * without its `/o/{alias}` prefix), so the canonical must point at the org's
 * authoritative URL (`org_url`: its own subdomain, or `{district}/o/{alias}`
 * when it lives under the district domain) — not at whichever host served the
 * request. When the org cannot be resolved the canonical is self-referencing
 * (the request URL as served) rather than guessing a prefix from store state,
 * which can be stale after cross-org client-side navigation. The trailing
 * slash matches the URL format of the backend-generated sitemap.xml.
 * @param {{ path: string, domain: string, alias: string|null, organizations: Array<object>, query?: object }} params
 * @returns {string|null} - The absolute canonical URL, or null when it cannot be determined.
 */
export const buildCanonicalUrl = ({ path, domain, alias, organizations, query }) => {
  const org = resolveCanonicalOrg({ alias, domain, organizations });

  let base = null;
  let relativePath = path || '/';
  // An org_url equal to its path_prefix is not an absolute location (see formatOrgLink in components-next).
  if (org?.org_url && org.org_url !== org.path_prefix) {
    base = `https://${org.org_url}`;
    relativePath = relativePath.replace(ALIAS_PREFIX_PATTERN, '');
  } else if (domain) {
    base = `https://${domain}`;
  }
  if (!base) return null;

  if (relativePath && !relativePath.startsWith('/')) {
    relativePath = `/${relativePath}`;
  }

  const href = `${base}${relativePath}`;
  return `${href.endsWith('/') ? href : `${href}/`}${buildCanonicalQueryString(query)}`;
};
